// OP-3619 (D7) — A cadeia que responde pelo estado ATUAL da peça.
//
// O razão já diz tudo, mas em ordem cronológica: quem abre a ficha precisa
// atravessar dezenas de lançamentos para descobrir de que contrato aquela peça
// saiu. A faixa responde isso numa linha — e a regra de QUAL cadeia mostrar é
// o miolo do problema, porque "a última referência REM-" não é a mesma coisa
// que "o documento que responde pela peça hoje".
import { HistoricoUnidadeRow, RemessaDetalhe } from '../../../types/estoque'
import { TIPO_MOVIMENTO_LABEL } from './Razao/regras'
import { TipoDocumento, documentoDaReferencia, rotaDaReferencia, rotuloDoDocumento } from './referencia'

export type TipoElo = TipoDocumento | 'AVULSO' | 'PECA'

export interface Elo {
  tipo: TipoElo
  rotulo: string
  codigo: string | null
  /** Id para o link; null desenha texto puro. */
  id: number | null
  /** Por que este elo não existe. Preenchido => elo apagado. */
  ausente: string | null
  atual: boolean
}

export interface Ancora {
  tipo: TipoDocumento | 'AVULSO'
  codigo: string | null
  id: number | null
  movimentoId: number
  extravio: boolean
}

export interface LeituraDoHistorico {
  ancora: Ancora | null
  /** O lançamento válido mais recente: é ele que diz onde a peça está hoje. */
  ultimo: HistoricoUnidadeRow | null
  vazio: boolean
}

export type RemessaDaCadeia = Pick<
  RemessaDetalhe,
  'id' | 'codigo' | 'status' | 'solicitacaoId' | 'solicitacaoNumero' | 'vinculoId' | 'vinculoCodigo'
>

export type LeituraRemessa =
  | { estado: 'carregando' }
  | { estado: 'fora' }
  | { estado: 'falhou' }
  | { estado: 'pronta'; remessa: RemessaDaCadeia }

export interface DepoisDaAncora {
  movimento: string
  codigo: string | null
  rota: string | null
}

export interface Cadeia {
  elos: Elo[]
  /** O que aconteceu com a peça DEPOIS da âncora, quando algo aconteceu. */
  depois: DepoisDaAncora | null
}

const rotulo = (tipo: TipoElo): string =>
  tipo === 'AVULSO' ? 'Lançamento avulso' : tipo === 'PECA' ? 'Peça' : rotuloDoDocumento(tipo)

const maisNovoPrimeiro = (a: HistoricoUnidadeRow, b: HistoricoUnidadeRow): number => {
  if (a.criadoEm !== b.criadoEm) return a.criadoEm < b.criadoEm ? 1 : -1
  return b.id - a.id
}

/**
 * Lançamento que ainda vale: estorno não conta, e nem o que foi estornado.
 * Um EXTRAVIO revertido significa que a peça reapareceu.
 */
const vale = (m: HistoricoUnidadeRow): boolean => m.movimentoEstornoId == null && m.estornadoPorId == null

/**
 * A âncora é o documento mais recente que explica a peça: qualquer EXTRAVIO
 * válido — da remessa, da contagem, da transferência ou avulso — ou, não
 * havendo, a última remessa. Um extravio de contagem vence a remessa antiga:
 * apontar a remessa encerrada de meses atrás mandaria quem procura para o
 * lugar errado.
 */
export function lerHistorico(historico: HistoricoUnidadeRow[]): LeituraDoHistorico {
  const validos = historico.filter(vale).sort(maisNovoPrimeiro)
  let ancora: Ancora | null = null
  for (const m of validos) {
    const doc = documentoDaReferencia(m.referenciaTipo, m.referenciaId, m.referenciaDocumento)
    if (m.tipo === 'EXTRAVIO') {
      const tipo = doc ?? 'AVULSO'
      ancora = { tipo, codigo: m.referenciaId ?? null, id: doc ? m.referenciaRefId ?? null : null, movimentoId: m.id, extravio: true }
      break
    }
    if (doc === 'REMESSA') {
      ancora = { tipo: 'REMESSA', codigo: m.referenciaId ?? null, id: m.referenciaRefId ?? null, movimentoId: m.id, extravio: false }
      break
    }
  }
  return { ancora, ultimo: validos[0] ?? null, vazio: historico.length === 0 }
}

/** A remessa só é buscada quando a âncora é uma remessa com registro. */
export const remessaParaBuscar = (h: LeituraDoHistorico): number | null =>
  h.ancora && h.ancora.tipo === 'REMESSA' ? h.ancora.id : null

/**
 * "Não encontrada" é o que o backend responde para remessa fora do escopo
 * (REMESSA_NAO_ENCONTRADA, 400); qualquer outra falha é falha, e a faixa não
 * pode chamá-la de escopo.
 */
export function leituraDaRemessa(q: { falhou: boolean; codigoErro?: string | null; remessa: RemessaDaCadeia | null }): LeituraRemessa {
  if (q.falhou) return q.codigoErro === 'REMESSA_NAO_ENCONTRADA' ? { estado: 'fora' } : { estado: 'falhou' }
  if (q.remessa) return { estado: 'pronta', remessa: q.remessa }
  return { estado: 'carregando' }
}

const elo = (tipo: TipoElo, codigo: string | null, id: number | null, ausente: string | null): Elo =>
  ({ tipo, rotulo: rotulo(tipo), codigo, id, ausente, atual: false })

const destacar = (elos: Elo[], tipo: TipoElo): Elo[] =>
  elos.map((e) => (e.tipo === tipo ? { ...e, atual: true } : e))

const descrever = (m: HistoricoUnidadeRow): DepoisDaAncora => ({
  movimento: TIPO_MOVIMENTO_LABEL[m.tipo] ?? m.tipo,
  codigo: m.referenciaId ?? null,
  rota: rotaDaReferencia(m.referenciaTipo, m.referenciaRefId ?? null, m.referenciaId, m.referenciaDocumento),
})

function motivoAcimaDaRemessa(ancora: Ancora, remessa: LeituraRemessa): string | null {
  if (ancora.id == null) return 'remessa antiga, sem registro para abrir'
  if (remessa.estado === 'fora') return 'remessa fora do seu escopo ou já removida'
  if (remessa.estado === 'falhou') return 'não foi possível carregar a remessa'
  if (remessa.estado === 'carregando' || Number(remessa.remessa.id) !== Number(ancora.id)) return 'carregando'
  return null
}

export interface EntradaCadeia {
  historico: LeituraDoHistorico
  serial: string
  remessa: LeituraRemessa
}

/**
 * O destaque sai do razão, não do local: `local_base_id` só é gravado no
 * cadastro, e manutenção e transferência também tiram a peça da base. Se algo
 * aconteceu DEPOIS da âncora, a âncora é passado — o destaque vai para a peça
 * e a faixa diz o que veio depois.
 */
export function cadeiaDaPeca({ historico, serial, remessa }: EntradaCadeia): Cadeia {
  const { ancora, ultimo } = historico
  const peca = elo('PECA', serial, null, null)

  if (!ancora) {
    const motivo = historico.vazio ? 'sem lançamento no razão' : 'nunca saiu em remessa'
    return {
      elos: destacar([elo('CONTRATO', null, null, motivo), elo('PEDIDO', null, null, motivo), elo('REMESSA', null, null, motivo), peca], 'PECA'),
      depois: null,
    }
  }

  const ancoraEhOUltimo = !!ultimo && ultimo.id === ancora.movimentoId
  const depois = !ancoraEhOUltimo && ultimo ? descrever(ultimo) : null

  if (ancora.tipo !== 'REMESSA') {
    const elos = [elo(ancora.tipo, ancora.codigo, ancora.id, null), peca]
    return { elos: destacar(elos, ancoraEhOUltimo ? ancora.tipo : 'PECA'), depois }
  }

  const motivo = motivoAcimaDaRemessa(ancora, remessa)
  const r = remessa.estado === 'pronta' ? remessa.remessa : null

  const contrato = motivo
    ? elo('CONTRATO', null, null, motivo)
    : r && (r.vinculoCodigo || r.vinculoId)
      ? elo('CONTRATO', r.vinculoCodigo ?? null, r.vinculoId ?? null, null)
      : elo('CONTRATO', null, null, 'remessa sem contrato')

  const pedido = motivo
    ? elo('PEDIDO', null, null, motivo)
    : r && (r.solicitacaoNumero || r.solicitacaoId)
      ? elo('PEDIDO', r.solicitacaoNumero ?? null, r.solicitacaoId ?? null, null)
      : elo('PEDIDO', null, null, 'remessa avulsa, sem pedido')

  const elos = [contrato, pedido, elo('REMESSA', ancora.codigo, ancora.id, null), peca]

  if (!ancoraEhOUltimo) return { elos: destacar(elos, 'PECA'), depois }
  if (ancora.extravio) return { elos: destacar(elos, 'REMESSA'), depois: null }
  const voltou = ultimo?.situacaoApos === 'EM_ESTOQUE'
  return { elos: destacar(elos, voltou ? 'PECA' : 'REMESSA'), depois: null }
}
